import { useState, useEffect } from 'react';
import { Drawer, Text, Loader, Group, Badge, Image, Stack, Paper, Title, Button } from '@mantine/core';
import { IconDownload } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import CampaignReport from "../../api/CampaignReport";

interface AdPreviewDrawerProps {
  opened: boolean;
  onClose: () => void;
  platform: 'facebook' | 'google';
  adName: string;
  adId: string;
}

export function AdPreviewDrawer({
  opened,
  onClose,
  platform,
  adName,
  adId
}: AdPreviewDrawerProps) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [adData, setAdData] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (opened && adId && platform) {
      fetchAdPreview();
    }
  }, [opened, adId, platform]);

  const fetchAdPreview = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await CampaignReport.getadpreview({
        platform: platform,
        ad_id: adId
      });

      if (!response.ok) {
        if (response.status === 401 || response.status === 400) {
          navigate('/logout');
          return;
        }
        throw new Error('Failed to fetch ad preview');
      }

      const data = await response.json();

      if (data?.status === 1) {
        setAdData(data.data);
      } else {
        setError(data?.message || 'Failed to load ad preview');
      }
    } catch (err) {
      setError('An error occurred while loading the ad preview');
      console.error('Ad preview error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Helper to convert YouTube URL to embed format
  const getYouTubeEmbedUrl = (url: string): string | null => {
    const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)/);
    return match ? `https://www.youtube.com/embed/${match[1]}` : null;
  };

  // Render Google text details (headline, description, primary_text)
  const renderGoogleTextDetails = (preview: any) => {
    const hasText = preview.headline || preview.description || preview.primary_text;
    if (!hasText) return null;
    return (
      <Stack spacing="sm">
        {preview.primary_text && (
          <Paper p="md" withBorder>
            <Text size="sm" color="dimmed" mb={4}>Primary Text</Text>
            <Text>{preview.primary_text}</Text>
          </Paper>
        )}
        {preview.headline && (
          <Paper p="md" withBorder>
            <Text size="sm" color="dimmed" mb={4}>Headline</Text>
            <Text weight={600} size="lg">{preview.headline}</Text>
          </Paper>
        )}
        {preview.description && (
          <Paper p="md" withBorder>
            <Text size="sm" color="dimmed" mb={4}>Description</Text>
            <Text>{preview.description}</Text>
          </Paper>
        )}
      </Stack>
    );
  };

  const renderPreview = () => {
    if (!adData?.preview) return null;

    const { preview } = adData;

    // Facebook: Use HTML preview only (shows everything including video, text, images)
    if (preview.type === 'html' || adData.platform === 'facebook') {
      if (preview.html) {
        return (
          <Stack spacing="sm">
            <Paper p="md" withBorder>
              <div
                dangerouslySetInnerHTML={{ __html: preview.html }}
                style={{ width: '100%', minHeight: '400px' }}
              />
            </Paper>
            {preview.video_url && (
              <Button
                component="a"
                href={preview.video_url}
                target="_blank"
                rel="noopener noreferrer"
                download
                leftIcon={<IconDownload size={16} />}
                variant="light"
              >
                Download Video
              </Button>
            )}
            {preview.image_url && (
              <Button
                component="a"
                href={preview.image_url}
                target="_blank"
                rel="noopener noreferrer"
                download
                leftIcon={<IconDownload size={16} />}
                variant="light"
              >
                Download Image
              </Button>
            )}
            {adData.platform === 'facebook' && !preview.video_url && !preview.image_url && (
              <Stack spacing={4}>
                <Button leftIcon={<IconDownload size={16} />} variant="light" disabled>
                  {preview.has_video ? 'Download Video' : 'Download'}
                </Button>
                <Text size="sm" color="dimmed">Facebook doesn't allow downloading this ad format.</Text>
              </Stack>
            )}
          </Stack>
        );
      }
      return <Text color="dimmed">No preview available</Text>;
    }

    // Google: Render based on ad type (image, video, text)
    switch (preview.type) {
      case 'image':
        return (
          <Stack spacing="sm">
            <Paper p="md" withBorder>
              {preview.media_url && (
                <Image
                  src={preview.media_url}
                  alt={adData.ad_name}
                  radius="md"
                  fit="contain"
                  height={400}
                />
              )}
            </Paper>
            {preview.image_url && (
              <Button
                component="a"
                href={preview.image_url}
                target="_blank"
                rel="noopener noreferrer"
                download
                leftIcon={<IconDownload size={16} />}
                variant="light"
              >
                Download Image
              </Button>
            )}
            {renderGoogleTextDetails(preview)}
          </Stack>
        );

      case 'video': {
        const youtubeEmbedUrl = preview.media_url ? getYouTubeEmbedUrl(preview.media_url) : null;
        return (
          <Stack spacing="sm">
            <Paper p="md" withBorder>
              {youtubeEmbedUrl ? (
                <iframe
                  src={youtubeEmbedUrl}
                  style={{ width: '100%', height: '400px', border: 'none' }}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              ) : preview.media_url ? (
                <video
                  src={preview.media_url}
                  controls
                  style={{ width: '100%', maxHeight: '500px' }}
                />
              ) : (
                <Text color="dimmed">Video preview not available</Text>
              )}
            </Paper>
            {renderGoogleTextDetails(preview)}
          </Stack>
        );
      }

      case 'text':
      default:
        return renderGoogleTextDetails(preview);
    }
  };

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size="xl"
      withCloseButton={false}
    >
      <Drawer.Header>
        <Group w="100%" position="apart" align="center">
          <Drawer.Title>
            <Group>
              <Text span fw={600}>{adName}</Text>
              <Badge color={platform === 'facebook' ? 'blue' : 'red'}>
                {platform === 'facebook' ? 'Facebook' : 'Google'}
              </Badge>
            </Group>
          </Drawer.Title>
          <Drawer.CloseButton />
        </Group>
      </Drawer.Header>

      <Stack p="md" spacing="md">
        {loading && (
          <Group position="center" py="xl">
            <Loader size="lg" />
            <Text>Loading ad preview...</Text>
          </Group>
        )}

        {error && !loading && (
          <Paper p="md" withBorder>
            <Text color="red">{error}</Text>
          </Paper>
        )}

        {adData && !loading && !error && (
          <>
            <Paper p="md" withBorder>
              <Stack spacing="xs">
                <Group position="apart">
                  <Text size="sm" color="dimmed">Status</Text>
                  <Badge color={adData.status === 'ACTIVE' || adData.status === 'ENABLED' ? 'green' : 'gray'}>
                    {adData.status}
                  </Badge>
                </Group>
                <Group position="apart">
                  <Text size="sm" color="dimmed">Platform</Text>
                  <Text size="sm" style={{ textTransform: 'capitalize' }}>{adData.platform}</Text>
                </Group>
                <Group position="apart">
                  <Text size="sm" color="dimmed">Ad ID</Text>
                  <Text size="sm" style={{ fontFamily: 'monospace' }}>{adData.ad_id}</Text>
                </Group>
              </Stack>
            </Paper>

            <Title order={4}>Creative Preview</Title>
            {renderPreview()}
          </>
        )}
      </Stack>
    </Drawer>
  );
}
